import { useMemo } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import toast from 'react-hot-toast';
import { GET_STOCK_LOCATIONS } from '../graphql/queries/stockLocations';
import {
  CREATE_STOCK_LOCATION,
  DELETE_STOCK_LOCATION,
  RENAME_STOCK_LOCATION,
} from '../graphql/mutations/stockLocations';

const refetchLocations = { refetchQueries: ['GetStockLocations'], awaitRefetchQueries: true };

// Stock locations live in stock_locations, separate from crypto token locations.
const useStockLocations = (portfolioId, { skip = false } = {}) => {
  const { data, loading } = useQuery(GET_STOCK_LOCATIONS, {
    variables: { portfolioId },
    skip: skip || !portfolioId,
  });
  const [createMutation] = useMutation(CREATE_STOCK_LOCATION, refetchLocations);
  const [renameMutation] = useMutation(RENAME_STOCK_LOCATION, refetchLocations);
  const [deleteMutation] = useMutation(DELETE_STOCK_LOCATION, refetchLocations);
  const locations = useMemo(() => data?.stockLocations || [], [data]);

  const createLocation = async (name) => {
    const trimmed = (name || '').trim();
    if (!trimmed || !portfolioId) return null;
    const existing = locations.find((location) => location.name.toLowerCase() === trimmed.toLowerCase());
    if (existing) return existing;
    try {
      const result = await createMutation({ variables: { portfolioId, name: trimmed } });
      const created = { id: result.data.createStockLocation, name: trimmed };
      toast.success(`Created location ${trimmed}`);
      return created;
    } catch (error) {
      toast.error(error?.message || 'Failed to create location');
      return null;
    }
  };

  const renameLocation = async (locationId, name) => {
    const trimmed = (name || '').trim();
    if (!locationId || !trimmed || !portfolioId) return false;
    try {
      await renameMutation({ variables: { portfolioId, locationId, name: trimmed } });
      toast.success('Location renamed');
      return true;
    } catch (error) {
      toast.error(error?.message || 'Failed to rename location');
      return false;
    }
  };

  const deleteLocation = async (location) => {
    if (!location?.id || !portfolioId) return false;
    try {
      const result = await deleteMutation({ variables: { portfolioId, locationId: location.id } });
      if (result.data?.deleteStockLocation !== true) throw new Error('Location was not deleted');
      toast.success(`Deleted location ${location.name || ''}`.trim());
      return true;
    } catch (error) {
      toast.error(error?.message || 'Failed to delete location');
      return false;
    }
  };

  return { locations, loading, createLocation, renameLocation, deleteLocation };
};

export default useStockLocations;
